import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import EstoreChrome from "../components/EstoreChrome";
import { brand } from "../data/estoreContent";
import { ESTORE_ROUTES } from "../data/estorePaths";
import { useEstoreCart } from "../hooks/useEstoreCart";
import { removeEstoreCartLine, updateEstoreCartQty } from "../utils/estoreCart";

function EstoreCart() {
  const navigate = useNavigate();
  const { lines, subtotal } = useEstoreCart();

  return (
    <Box sx={{ mt: 7, minHeight: "100vh", bgcolor: brand.bg }}>
      <EstoreChrome title="Your bag" />
      <Container maxWidth="md" sx={{ py: 4 }}>
        {lines.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Box sx={{ textAlign: "center", py: 10 }}>
              <ShoppingCart size={56} strokeWidth={1.25} />
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>Your bag is empty</Typography>
              <Button variant="contained" sx={{ mt: 2, borderRadius: 999, textTransform: "none", fontWeight: 800 }} onClick={() => navigate(ESTORE_ROUTES.shop)}>
                Continue shopping
              </Button>
            </Box>
          </motion.div>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {lines.map((item, i) => (
                <motion.div key={item.productId} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card elevation={0} sx={{ mb: 2, border: `1px solid ${brand.stroke}`, borderRadius: 3 }}>
                    <CardContent>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.title}
                          sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 2 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={800}>{item.title}</Typography>
                          <Typography variant="body2" sx={{ color: brand.muted }}>₹{item.unitPrice} each</Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <IconButton size="small" onClick={() => updateEstoreCartQty(item.productId, item.qty - 1)}>
                              <Minus size={16} />
                            </IconButton>
                            <Typography fontWeight={700}>{item.qty}</Typography>
                            <IconButton size="small" onClick={() => updateEstoreCartQty(item.productId, item.qty + 1)}>
                              <Plus size={16} />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => removeEstoreCartLine(item.productId)} sx={{ ml: 1 }}>
                              <Trash2 size={16} />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography fontWeight={800}>₹{item.unitPrice * item.qty}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={0} sx={{ border: `1px solid ${brand.stroke}`, borderRadius: 3, p: 2, position: "sticky", top: 96 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>Summary</Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ color: brand.muted }}>Subtotal</Typography>
                  <Typography fontWeight={700}>₹{subtotal}</Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography fontWeight={800}>Total</Typography>
                  <Typography fontWeight={800}>₹{subtotal}</Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate(ESTORE_ROUTES.checkout)}
                  sx={{ borderRadius: 999, py: 1.25, fontWeight: 800, textTransform: "none" }}
                >
                  Checkout
                </Button>
                <Button fullWidth sx={{ mt: 1, textTransform: "none", fontWeight: 700 }} onClick={() => navigate(ESTORE_ROUTES.shop)}>
                  Keep browsing
                </Button>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
}

export default EstoreCart;
